import React, { useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaArrowLeft, FaPen } from "react-icons/fa";
import { workspaceApi } from "pages/app/workspace/services";
import { HeroSurfacePanel } from "pages/app/workspace/sections/styles/workspace-section.styles";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import {
  DataTableSkeleton,
  EmptyState,
  FixturesShowcase,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
  StatusPill,
} from "components/admin";
import { DataTable } from "components/molecules/table/table";
import { useAuth } from "context/auth-context";
import { formatDateTime } from "utils/admin";
import { formatPortalLabel, hasPortalAction } from "utils/portal-access";
import {
  SchoolRosterProfileSecondaryButton,
  SchoolRosterProfilePrimaryButton,
  SchoolRosterProfileTextGroup,
  SchoolRosterProfileGridLayoutTertiary,
  SchoolRosterProfileSectionTertiary,
  SchoolRosterProfileSectionQuaternary,
  SchoolRosterProfileContentStackSecondary,
  SchoolRosterProfileSectionQuinary,
  SchoolRosterProfileGridLayoutQuaternary,
  SchoolRosterProfileInfoRowSecondary,
  SchoolRosterProfileGridLayoutQuinary,
  SchoolRosterProfileContentStackTertiary,
  SchoolRosterProfileSectionSenary,
  SchoolRosterProfileGridLayout,
  SchoolRosterProfileGridLayoutSenary,
  SchoolRosterProfileSection,
  SchoolRosterProfileGridLayoutSecondary,
  SchoolRosterProfileContentStack,
  SchoolRosterProfileInfoRow,
  SchoolRosterProfileSectionSecondary,
  SchoolRosterProfileThumbnailImage,
  SchoolRosterProfilePreviewImage,
  SchoolRosterProfileTitleHeading,
  SchoolRosterProfileEyebrowText,
  SchoolRosterProfileMetaText,
  SchoolRosterProfileEyebrowTextSecondary,
  SchoolRosterProfileTitleText,
  SchoolRosterProfileBodyText,
  SchoolRosterProfileLabelText,
  SchoolRosterProfileMetaTextSecondary,
  SchoolRosterProfileBodyTextSecondary,
  SchoolRosterProfileStatusBadge,
  SchoolRosterProfileInlineText,
} from "pages/app/workspace/styles/school-roster-profile.page.inline.styles";
const SchoolRosterProfilePage: React.FC = () => {
  const { schoolId, matchId, id } = useParams<{
    schoolId: string;
    matchId: string;
    id: string;
  }>();
  const history = useHistory();
  const location = useLocation<{
    backTo?: string;
  }>();
  const { portalAccess } = useAuth();
  const schoolQuery = useQuery({
    queryKey: ["school-roster-profile-school", schoolId],
    queryFn: () => workspaceApi.schools.get(schoolId),
    enabled: Boolean(schoolId),
  });
  const matchQuery = useQuery({
    queryKey: ["school-roster-profile-match", matchId],
    queryFn: () => workspaceApi.schoolMatches.get(matchId),
    enabled: Boolean(matchId),
  });
  const rosterQuery = useQuery({
    queryKey: ["school-roster-profile", matchId, id],
    queryFn: () => workspaceApi.schoolRosters.get(matchId, id),
    enabled: Boolean(matchId) && Boolean(id),
  });
  const schoolPlayersQuery = useQuery({
    queryKey: ["school-roster-profile-players", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const roster = rosterQuery.data;
  const match = matchQuery.data;
  const playersById = useMemo(
    () =>
      Object.fromEntries(
        (schoolPlayersQuery.data || []).map((player: any) => [String(player.id), player]),
      ),
    [schoolPlayersQuery.data],
  );
  const selectedTeam = useMemo(() => {
    const teams = [match?.home_school_team, match?.away_school_team].filter(Boolean);
    return teams.find((team: any) => String(team.id) === String(roster?.school_team_id)) || null;
  }, [match?.away_school_team, match?.home_school_team, roster?.school_team_id]);
  const opponentTeam = useMemo(() => {
    const teams = [match?.home_school_team, match?.away_school_team].filter(Boolean);
    return teams.find((team: any) => String(team.id) !== String(roster?.school_team_id)) || null;
  }, [match?.away_school_team, match?.home_school_team, roster?.school_team_id]);
  const rosterPlayers = roster?.school_match_roster_players || [];
  const starterCount = rosterPlayers.filter((player: any) => Boolean(player.starter)).length;
  const captainCount = rosterPlayers.filter(
    (player: any) => Boolean(player.captain) || Boolean(player.vice_captain),
  ).length;
  const selectionStatusEntries = Object.entries(
    rosterPlayers.reduce((accumulator: Record<string, number>, player: any) => {
      const key = String(player.status || "selected");
      accumulator[key] = (accumulator[key] || 0) + 1;
      return accumulator;
    }, {}),
  ) as Array<[string, number]>;
  const metadataEntries = Object.entries(roster?.metadata || {});
  const canManageRoster = hasPortalAction(portalAccess, "school_match_rosters.manage");
  const isLoading =
    schoolQuery.isLoading ||
    matchQuery.isLoading ||
    rosterQuery.isLoading ||
    schoolPlayersQuery.isLoading;
  const rosterTitle = selectedTeam?.name || roster?.school_team_name || "Match roster";
  const matchLabel =
    match?.competition_name ||
    `${match?.home_school_team?.name || "Home"} vs ${match?.away_school_team?.name || "Away"}`;
  const backTo =
    location.state?.backTo ||
    (matchId
      ? `/workspace/school-matches/${matchId}?tab=rosters`
      : "/workspace/schedule?view=list");
  const manageRostersPath = matchId
    ? `/workspace/school-matches/${matchId}?tab=rosters`
    : "/workspace/schedule?view=list";

  const headerActions = useMemo(
    () => (
      <>
        <SchoolRosterProfileSecondaryButton onClick={() => history.push(backTo)} type="button">
          <FaArrowLeft size={12} />
          Back
        </SchoolRosterProfileSecondaryButton>
        {canManageRoster ? (
          <SchoolRosterProfilePrimaryButton
            onClick={() => history.push(manageRostersPath)}
            type="button"
          >
            <FaPen size={12} />
            Manage rosters
          </SchoolRosterProfilePrimaryButton>
        ) : null}
      </>
    ),
    [backTo, canManageRoster, history, manageRostersPath],
  );

  return (
    <AppLayout route="/workspace/schedule">
      <SchoolOpsHeader actions={headerActions} title={rosterTitle} />

      {isLoading ? (
        <StatsGridSkeleton />
      ) : roster ? (
        <SchoolRosterProfileGridLayout>
          <StatCard label="Team" value={roster.school_team_name || "Unknown"} />
          <StatCard label="Status" value={<StatusPill value={roster.status || "draft"} />} />
          <StatCard label="Players" value={rosterPlayers.length} />
          <StatCard
            label="Starters"
            value={starterCount}
            helper={`${captainCount} captain roles`}
          />
        </SchoolRosterProfileGridLayout>
      ) : null}

      {!isLoading && !roster ? (
        <SchoolRosterProfileSection>
          <EmptyState
            description="This roster could not be loaded from the selected fixture."
            title="Roster not found"
          />
        </SchoolRosterProfileSection>
      ) : (
        <>
          <SchoolRosterProfileGridLayoutSecondary>
            <SectionCard
              subtitle="Team identity, submission timing, and roster notes for this selected squad."
              title="Roster Overview"
            >
              {roster ? (
                <SchoolRosterProfileContentStack>
                  <HeroSurfacePanel>
                    <SchoolRosterProfileInfoRow>
                      <SchoolRosterProfileSectionSecondary>
                        <SchoolRosterProfilePreviewImage
                          alt={roster.school_team_name || "Team"}
                          fallbackLabel={roster.school_team_name}
                          src={roster.school_team_image_url}
                          variant="team"
                        />
                      </SchoolRosterProfileSectionSecondary>
                      <SchoolRosterProfileTextGroup>
                        <SchoolRosterProfileEyebrowText>
                          Selected Team
                        </SchoolRosterProfileEyebrowText>
                        <SchoolRosterProfileTitleHeading>
                          {roster.school_team_name || "Unknown"}
                        </SchoolRosterProfileTitleHeading>
                        <SchoolRosterProfileMetaText>
                          {roster.school_name || selectedTeam?.school_name || "School pending"}
                        </SchoolRosterProfileMetaText>
                      </SchoolRosterProfileTextGroup>
                    </SchoolRosterProfileInfoRow>
                  </HeroSurfacePanel>

                  <SchoolRosterProfileGridLayoutTertiary>
                    <SchoolRosterProfileSectionTertiary>
                      <SchoolRosterProfileEyebrowTextSecondary>
                        Submitted by
                      </SchoolRosterProfileEyebrowTextSecondary>
                      <SchoolRosterProfileTitleText>
                        {roster.submitted_by_name || "Not submitted"}
                      </SchoolRosterProfileTitleText>
                    </SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileSectionTertiary>
                      <SchoolRosterProfileEyebrowTextSecondary>
                        Submitted at
                      </SchoolRosterProfileEyebrowTextSecondary>
                      <SchoolRosterProfileTitleText>
                        {formatDateTime(roster.submitted_at)}
                      </SchoolRosterProfileTitleText>
                    </SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileSectionTertiary>
                      <SchoolRosterProfileEyebrowTextSecondary>
                        Locked at
                      </SchoolRosterProfileEyebrowTextSecondary>
                      <SchoolRosterProfileTitleText>
                        {formatDateTime(roster.locked_at)}
                      </SchoolRosterProfileTitleText>
                    </SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileSectionTertiary>
                      <SchoolRosterProfileEyebrowTextSecondary>
                        Opponent
                      </SchoolRosterProfileEyebrowTextSecondary>
                      <SchoolRosterProfileTitleText>
                        {opponentTeam?.name || "Fixture pending"}
                      </SchoolRosterProfileTitleText>
                    </SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileSectionQuaternary>
                      <SchoolRosterProfileEyebrowTextSecondary>
                        Selection notes
                      </SchoolRosterProfileEyebrowTextSecondary>
                      <SchoolRosterProfileBodyText>
                        {roster.notes || "No roster notes were provided for this submission."}
                      </SchoolRosterProfileBodyText>
                    </SchoolRosterProfileSectionQuaternary>
                  </SchoolRosterProfileGridLayoutTertiary>
                </SchoolRosterProfileContentStack>
              ) : (
                <DataTableSkeleton columns={2} rows={4} showThumbnail />
              )}
            </SectionCard>

            <SectionCard
              subtitle="Fixture context for the match this roster belongs to, including status and live-link signals."
              title="Match Context"
            >
              <SchoolRosterProfileContentStackSecondary>
                <SchoolRosterProfileGridLayoutTertiary>
                  <SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileEyebrowTextSecondary>
                      Competition
                    </SchoolRosterProfileEyebrowTextSecondary>
                    <SchoolRosterProfileTitleText>{matchLabel}</SchoolRosterProfileTitleText>
                  </SchoolRosterProfileSectionTertiary>
                  <SchoolRosterProfileSectionTertiary>
                    <SchoolRosterProfileEyebrowTextSecondary>
                      Match status
                    </SchoolRosterProfileEyebrowTextSecondary>
                    <SchoolRosterProfileSectionQuinary>
                      <StatusPill value={match?.status || "proposed"} />
                    </SchoolRosterProfileSectionQuinary>
                  </SchoolRosterProfileSectionTertiary>
                </SchoolRosterProfileGridLayoutTertiary>

                <FixturesShowcase
                  emptyDescription="Fixture information will appear here once the selected match can be loaded."
                  emptyTitle="No fixture context"
                  fixtures={match ? [match] : []}
                  isLoading={matchQuery.isLoading}
                />
              </SchoolRosterProfileContentStackSecondary>
            </SectionCard>
          </SchoolRosterProfileGridLayoutSecondary>

          <SchoolRosterProfileGridLayoutQuaternary>
            <SectionCard
              subtitle="The players currently selected in this roster, including roles, jersey numbers, and squad status."
              title="Squad Selection"
            >
              <DataTable
                canEdit={false}
                canRemove={false}
                columns={[
                  {
                    label: "Player",
                    accessor: (row: any) => {
                      const playerProfile = playersById[String(row.player_id)];
                      return (
                        <SchoolRosterProfileInfoRowSecondary>
                          <SchoolRosterProfileThumbnailImage
                            alt={row.player_name || "Player"}
                            fallbackLabel={row.player_name}
                            src={
                              playerProfile?.avatar_url ||
                              playerProfile?.image ||
                              playerProfile?.image_url
                            }
                            variant="athlete"
                          />
                          <div>
                            <SchoolRosterProfileLabelText>
                              {row.player_name || "Unknown"}
                            </SchoolRosterProfileLabelText>
                            <SchoolRosterProfileMetaTextSecondary>
                              {playerProfile?.school_grade || "Grade pending"}
                            </SchoolRosterProfileMetaTextSecondary>
                          </div>
                        </SchoolRosterProfileInfoRowSecondary>
                      );
                    },
                  },
                  {
                    label: "Position",
                    accessor: (row: any) => row.position || "Unknown",
                  },
                  {
                    label: "Jersey",
                    accessor: (row: any) => row.jersey_number || "Not set",
                  },
                  {
                    label: "Role",
                    accessor: (row: any) =>
                      row.captain
                        ? "Captain"
                        : row.vice_captain
                        ? "Vice captain"
                        : row.starter
                        ? "Starter"
                        : "Squad",
                  },
                  {
                    label: "Status",
                    accessor: (row: any) => <StatusPill value={row.status || "selected"} />,
                  },
                ]}
                data={rosterPlayers}
                emptyDescription="Roster players will appear here when this squad has selections."
                emptyTitle="No players selected"
                hideNav
                isLoading={rosterQuery.isLoading}
                loadingState={<DataTableSkeleton columns={5} rows={5} showThumbnail />}
                onView={(playerId) =>
                  history.push({
                    pathname: `/workspace/school-players/${schoolId}/${playerId}`,
                    state: {
                      backTo: location.pathname,
                    },
                  })
                }
                rowKey={(row: any) => row.player_id || row.id}
              />
            </SectionCard>

            <SectionCard
              subtitle="Selection mix and metadata carried on this roster submission."
              title="Selection Signals"
            >
              <SchoolRosterProfileGridLayoutQuinary>
                <SchoolRosterProfileContentStackTertiary>
                  <SchoolRosterProfileEyebrowTextSecondary>
                    Status breakdown
                  </SchoolRosterProfileEyebrowTextSecondary>
                  {selectionStatusEntries.length > 0 ? (
                    <SchoolRosterProfileSectionSenary>
                      {selectionStatusEntries.map(([status, count]) => (
                        <SchoolRosterProfileStatusBadge key={status}>
                          <SchoolRosterProfileInlineText>
                            {formatPortalLabel(status)}
                          </SchoolRosterProfileInlineText>
                          <span>{count}</span>
                        </SchoolRosterProfileStatusBadge>
                      ))}
                    </SchoolRosterProfileSectionSenary>
                  ) : (
                    <EmptyState
                      description="Status chips will appear here once the roster has player entries."
                      title="No selection status"
                    />
                  )}
                </SchoolRosterProfileContentStackTertiary>

                <SchoolRosterProfileContentStackTertiary>
                  <SchoolRosterProfileEyebrowTextSecondary>
                    Roster metadata
                  </SchoolRosterProfileEyebrowTextSecondary>
                  {metadataEntries.length > 0 ? (
                    <SchoolRosterProfileGridLayoutSenary>
                      {metadataEntries.map(([key, value]) => (
                        <SchoolRosterProfileSectionTertiary key={key}>
                          <SchoolRosterProfileEyebrowTextSecondary>
                            {formatPortalLabel(key)}
                          </SchoolRosterProfileEyebrowTextSecondary>
                          <SchoolRosterProfileBodyTextSecondary>
                            {typeof value === "object" ? JSON.stringify(value) : String(value)}
                          </SchoolRosterProfileBodyTextSecondary>
                        </SchoolRosterProfileSectionTertiary>
                      ))}
                    </SchoolRosterProfileGridLayoutSenary>
                  ) : (
                    <EmptyState
                      description="Optional travel, medical, or preparation flags will surface here when metadata is attached."
                      title="No metadata"
                    />
                  )}
                </SchoolRosterProfileContentStackTertiary>
              </SchoolRosterProfileGridLayoutQuinary>
            </SectionCard>
          </SchoolRosterProfileGridLayoutQuaternary>
        </>
      )}
    </AppLayout>
  );
};
export default SchoolRosterProfilePage;
